
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { GripVertical } from "lucide-react";

interface FeatureComparisonProps {
  title?: string;
  description?: string;
  beforeImage: string;
  beforeLabel?: string;
  afterImage: string;
  afterLabel?: string;
  badgeText?: string;
}

export function FeatureComparison({
  title = "Traditional vs AI-Augmented Development",
  description = "Drag the slider to see how our AI-powered process transforms development speed and quality compared to traditional methods.",
  beforeImage = "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7",
  beforeLabel = "Traditional Development",
  afterImage = "https://images.unsplash.com/photo-1461749280684-dccba630e2f6",
  afterLabel = "AI-Augmented Development",
  badgeText = "70% Faster"
}: FeatureComparisonProps) {
  const [inset, setInset] = useState<number>(50);
  const [onMouseDown, setOnMouseDown] = useState<boolean>(false);

  const onMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!onMouseDown) return;

    const rect = e.currentTarget.getBoundingClientRect();
    let x = 0;

    if ("touches" in e && e.touches.length > 0) {
      x = e.touches[0].clientX - rect.left;
    } else if ("clientX" in e) {
      x = e.clientX - rect.left;
    }
    
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setInset(percentage);
  };

  return (
    <div className="w-full py-20 lg:py-24">
      <div className="container mx-auto">
        <div className="flex flex-col gap-4">
          <div>
            <Badge variant="success" className="text-white">{badgeText}</Badge>
          </div>
          <div className="flex gap-2 flex-col">
            <h2 className="text-3xl md:text-5xl tracking-tighter lg:max-w-3xl font-bold">
              {title}
            </h2>
            <p className="text-lg max-w-xl lg:max-w-2xl leading-relaxed tracking-tight text-gray-300">
              {description}
            </p>
          </div>
          
          <div className="pt-12 w-full">
            <div
              className="relative aspect-video w-full h-full overflow-hidden rounded-2xl select-none border border-white/10"
              onMouseMove={onMouseMove}
              onMouseUp={() => setOnMouseDown(false)}
              onMouseLeave={() => setOnMouseDown(false)}
              onTouchMove={onMouseMove}
              onTouchEnd={() => setOnMouseDown(false)}
            >
              <div
                className="bg-brand-orange h-full w-1 absolute z-20 top-0 -ml-1 select-none"
                style={{
                  left: inset + "%",
                }}
              >
                <button
                  className="bg-brand-orange rounded-full hover:scale-110 transition-all w-10 h-10 select-none -translate-y-1/2 absolute top-1/2 -ml-5 z-30 cursor-ew-resize flex justify-center items-center shadow-lg"
                  onTouchStart={(e) => {
                    setOnMouseDown(true);
                    onMouseMove(e);
                  }}
                  onMouseDown={(e) => {
                    setOnMouseDown(true);
                    onMouseMove(e);
                  }}
                  onTouchEnd={() => setOnMouseDown(false)}
                  onMouseUp={() => setOnMouseDown(false)}
                >
                  <GripVertical className="h-4 w-4 text-white select-none" />
                </button>
              </div>
              
              {/* Before image (shown on left side) */}
              <img
                src={afterImage}
                alt="AI-Augmented Development"
                className="absolute left-0 top-0 z-10 w-full h-full aspect-video rounded-2xl select-none object-cover"
                style={{
                  clipPath: "inset(0 0 0 " + inset + "%)",
                }}
              />
              
              {/* After image (shown on right side) */}
              <img
                src={beforeImage}
                alt="Traditional Development"
                className="absolute left-0 top-0 w-full h-full aspect-video rounded-2xl select-none object-cover"
              />
              
              {/* Labels */}
              <div className="absolute bottom-4 left-4 z-30 bg-black/70 px-3 py-1 rounded-lg text-white text-sm font-medium backdrop-blur-sm">
                {beforeLabel}
              </div>
              <div className="absolute bottom-4 right-4 z-30 bg-black/70 px-3 py-1 rounded-lg text-white text-sm font-medium backdrop-blur-sm">
                {afterLabel}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
